import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  ExportButton,
  TopToolbar,
  SelectInput,
  TextInput,
  useRecordContext,
} from 'react-admin';
import {
  Typography,
  Box,
  Chip,
  Avatar,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const activationFilters = [
  <SelectInput
    key="status"
    source="status"
    label="Durum"
    choices={[
      { id: 'pending', name: 'Bekliyor' },
      { id: 'completed', name: 'Tamamlandı' },
      { id: 'cancelled', name: 'İptal' },
      { id: 'timeout', name: 'Zaman Aşımı' },
    ]}
    alwaysOn
  />,
  <TextInput key="service" source="service" label="Servis" alwaysOn />,
  <TextInput key="firebaseUid" source="firebaseUid" label="Kullanıcı ID" alwaysOn />,
];

const ListActions = () => (
  <TopToolbar>
    <ExportButton
      sx={{
        backgroundColor: '#7367f0',
        color: 'white',
        '&:hover': { backgroundColor: '#5549d9' },
      }}
    />
  </TopToolbar>
);

const StatusChip = () => {
  const record = useRecordContext();
  if (!record) return null;

  const statusMap: Record<string, { label: string; bg: string; color: string }> = {
    pending: { label: 'Bekliyor', bg: 'rgba(255, 159, 67, 0.12)', color: '#ff9f43' },
    waiting_code: { label: 'Kod Bekleniyor', bg: 'rgba(0, 207, 232, 0.12)', color: '#00cfe8' },
    completed: { label: 'Tamamlandı', bg: 'rgba(40, 199, 111, 0.12)', color: '#28c76f' },
    cancelled: { label: 'İptal', bg: 'rgba(234, 84, 85, 0.12)', color: '#ea5455' },
    timeout: { label: 'Zaman Aşımı', bg: 'rgba(103, 109, 125, 0.12)', color: '#676d7d' },
  };

  const status = statusMap[record.status] || {
    label: record.status,
    bg: 'rgba(103, 109, 125, 0.12)',
    color: '#676d7d',
  };

  return (
    <Chip
      label={status.label}
      size="small"
      sx={{
        backgroundColor: status.bg,
        color: status.color,
        fontWeight: 600,
        fontSize: '0.7rem',
      }}
    />
  );
};

const PhoneInfo = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          backgroundColor: 'rgba(115, 103, 240, 0.12)',
        }}
      >
        <PhoneIcon sx={{ fontSize: 16, color: '#7367f0' }} />
      </Avatar>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: '#d0d2d6', fontFamily: 'monospace' }}
      >
        {record.phoneNumber}
      </Typography>
    </Box>
  );
};

const CodeField = () => {
  const record = useRecordContext();
  if (!record) return null;

  if (!record.code) {
    return (
      <Typography
        variant="body2"
        sx={{ color: '#676d7d', fontStyle: 'italic', fontSize: '0.8rem' }}
      >
        —
      </Typography>
    );
  }

  return (
    <Chip
      icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
      label={record.code}
      size="small"
      onClick={() => navigator.clipboard.writeText(record.code)}
      sx={{
        backgroundColor: 'rgba(40, 199, 111, 0.12)',
        color: '#28c76f',
        fontWeight: 700,
        fontFamily: 'monospace',
        fontSize: '0.85rem',
        cursor: 'pointer',
      }}
    />
  );
};

const CostField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: record.cost > 0 ? '#28c76f' : '#676d7d',
      }}
    >
      ${record.cost?.toFixed(2) || '0.00'}
    </Typography>
  );
};

const DateField = () => {
  const record = useRecordContext();
  if (!record?.createdAt) return null;

  const date = new Date(record.createdAt);
  return (
    <Typography variant="body2" sx={{ color: '#d0d2d6', fontSize: '0.8rem' }}>
      {date.toLocaleDateString('tr-TR')} {date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
    </Typography>
  );
};

export const ActivationList = () => (
  <Box>
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#d0d2d6' }}>
        Aktivasyonlar
      </Typography>
      <Typography variant="body2" sx={{ color: '#676d7d', mt: 0.5 }}>
        Client tarafından yapılan numara isteklerini ve durumlarını takip edin
      </Typography>
    </Box>

    <List
      resource="admin/sms/activations"
      filters={activationFilters}
      actions={<ListActions />}
      sort={{ field: 'createdAt', order: 'DESC' }}
      sx={{
        '& .RaList-main': {
          backgroundColor: '#283046',
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      <Datagrid
        bulkActionButtons={false}
        sx={{
          '& .RaDatagrid-headerCell': {
            backgroundColor: '#242b3d',
            fontWeight: 600,
            color: '#676d7d',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
          '& .RaDatagrid-row:hover': {
            backgroundColor: '#242b3d',
          },
          '& .RaDatagrid-rowCell': {
            borderColor: '#404656',
          },
        }}
      >
        <TextField source="activationId" label="Aktivasyon ID" />
        <TextField source="firebaseUid" label="Kullanıcı" />
        <TextField source="service" label="Servis" />
        <FunctionField label="Telefon" render={() => <PhoneInfo />} />
        <FunctionField label="Kod" render={() => <CodeField />} />
        <FunctionField label="Maliyet" render={() => <CostField />} />
        <FunctionField label="Durum" render={() => <StatusChip />} />
        <TextField source="activationOperator" label="Operatör" />
        <FunctionField label="Tarih" render={() => <DateField />} />
      </Datagrid>
    </List>
  </Box>
);
